import React, { ReactElement, useMemo } from 'react';
import { RouterProvider, createBrowserRouter, RouteObject, Outlet } from 'react-router-dom';
import useUmami, { trackPage } from 'pwa-synergy-umami';
import { Component, RouteApi, AppRoute } from '../../types';
import api from '../../components/api';
import './RouteContainer.css';

export interface RouteContainerProps<T extends object> {
  components: Map<string, Component<T>>;
  titleBar?: (commands: T) => ReactElement;
  toolBar?: (commands: T) => ReactElement;
  statusBar?: (commands: T) => ReactElement;
  commands: (RouteApi: RouteApi) => T;
  routes: AppRoute[];
  routeNotFoundComponent?: string;
  hideTitleBar?: boolean;
  hideToolBar?: boolean;
  hideStatusBar?: boolean;
}

interface RouteAppProps<T extends object> {
  appCommands: T | undefined;
  hideTitleBar?: boolean;
  hideToolBar?: boolean;
  hideStatusBar?: boolean;
  titleBar?: (commands: T) => ReactElement;
  toolBar?: (commands: T) => ReactElement;
  statusBar?: (commands: T) => ReactElement;
}

const endsWithSlash = (text: string): boolean => {
  return text !== '' && text.charAt(text.length - 1) === '/';
};

const startsWithSlash = (text: string): boolean => {
  return text !== '' && text.charAt(0) === '/';
};

const RouteApp = <T extends object>(props: RouteAppProps<T>) => {
  const { appCommands, hideTitleBar, hideToolBar, hideStatusBar, titleBar, toolBar, statusBar } =
    props;

  return (
    <div className="routeContainer">
      {appCommands && !hideTitleBar && titleBar && titleBar(appCommands)}
      {appCommands && !hideToolBar && toolBar && toolBar(appCommands)}
      <Outlet />
      {appCommands && !hideStatusBar && statusBar && statusBar(appCommands)}
    </div>
  );
};

const RouteContainer = <T extends object>(props: RouteContainerProps<T>) => {
  const {
    components,
    titleBar,
    toolBar,
    statusBar,
    commands,
    routes,
    routeNotFoundComponent,
    hideTitleBar,
    hideToolBar,
    hideStatusBar,
  } = props;
  const track = useUmami({
    url: '/',
    title: `Router-${api.isMobile() ? 'Mobile' : 'Desktop'}`,
    disabledPageView: true,
  });

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const navigate = (route: string, props?: any) => {
    trackPage({ url: route });
    if (props) {
      sessionStorage.setItem('props', JSON.stringify(props));
    } else {
      sessionStorage.removeItem('props');
    }

    window.location.href =
      endsWithSlash(document.location.origin) || startsWithSlash(route)
        ? document.location.origin + route
        : document.location.origin + '/' + route;
  };

  const back = () => {
    track('back', window.location.pathname);
    window.history.back();
  };

  const appCommands = useMemo(
    () =>
      commands({
        navigate,
        back,
      }),
    [],
  );

  const CreateElement = (id: string) => {
    const propsString = sessionStorage.getItem('props');
    const props = propsString !== null ? JSON.parse(propsString) : undefined;
    const component = components.get(id);
    if (component) {
      return component.content({ id, commands: appCommands, props });
    }
    return <></>;
  };

  const routeNotFound = routeNotFoundComponent
    ? [
      {
        path:"*",
        Component: () => CreateElement(routeNotFoundComponent)
      }
    ] 
    : [];

  const mapRoutes = (routes: AppRoute[]): RouteObject[] => {
    return [
      {
        path: '/',
        element: (
          <RouteApp
            appCommands={appCommands}
            hideTitleBar={hideTitleBar}
            hideToolBar={hideToolBar}
            hideStatusBar={hideStatusBar}
            titleBar={titleBar}
            toolBar={toolBar}
            statusBar={statusBar}
          />
        ),
        children: [...routes.map((route) =>
          'index' in route
            ? {
                index: true,
                Component: () => CreateElement(route.component),
              }
            : {
                path: route.path,
                Component: () => CreateElement(route.component),
                children: route.children ? mapRoutes(route.children) : undefined,
              },
          ),
          ...routeNotFound
        ]
      },
    ];
  };
  const router = useMemo(
    () => createBrowserRouter(mapRoutes(routes)),
    [appCommands, hideTitleBar, hideToolBar, hideStatusBar],
  );

  return <RouterProvider router={router} />;
};

export default RouteContainer;
